import { Pipe, PipeTransform } from '@angular/core';
import { IntlMessageFormat } from 'intl-messageformat';
import { TranslateService } from '@ngx-translate/core';
import { SystemMessagesService } from '../services';

type ErrorArgs = { [key: string]: string | number | boolean };

@Pipe({ name: 'systemErrors' })
export class SystemErrorsPipe implements PipeTransform {
  constructor(
    private systemErrorsService: SystemMessagesService,
    private translateService: TranslateService,
  ) {}

  transform(value: string | undefined | null, args: ErrorArgs = {}): string {
    if (!value) {
      return value ?? '';
    }
    const error = this.systemErrorsService.systemMessages.find(
      (e) => e.id === value,
    );
    const message = error?.message ?? value;
    return this.formatMessage(message, args);
  }

  // Pipe output is sometimes a JSON object ({ Title, Content }); format each
  // string leaf so the outer JSON braces are never seen as ICU syntax.
  // Handles both plain JSON objects and double-stringified JSON (e.g. "{ ... }").
  private formatMessage(message: string, args: ErrorArgs): string {
    let trimmed = message.trim();

    // Unwrap double-stringified JSON: "{ \"key\": \"val\" }" → { "key": "val" }
    if (trimmed.startsWith('"') && trimmed.endsWith('"')) {
      try {
        const unwrapped = JSON.parse(trimmed);
        if (typeof unwrapped === 'string') {
          trimmed = unwrapped.trim();
        }
      } catch {
        /* not a JSON string — continue with original */
      }
    }

    if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
      try {
        const parsed = JSON.parse(trimmed);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          const out: Record<string, unknown> = {};
          for (const key of Object.keys(parsed)) {
            const value = parsed[key];
            out[key] =
              typeof value === 'string'
                ? this.formatString(value, args)
                : value;
          }
          return JSON.stringify(out);
        }
      } catch {
        /* not JSON — fall through to formatting the raw string */
      }
    }
    return this.formatString(trimmed, args);
  }

  private formatString(text: string, args: ErrorArgs): string {
    let result = text;
    // Only run ICU on strings that use it; plain [key] messages pass through.
    if (result.includes('{')) {
      try {
        result = new IntlMessageFormat(
          result,
          this.translateService.currentLang || 'en',
        ).format(args) as string;
      } catch {
        /* invalid ICU — keep the raw text */
      }
    }
    // Backward-compatible [key] placeholder replacement.
    const escapeRegExp = (value: string) =>
      value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    Object.keys(args).forEach((key) => {
      const pattern = new RegExp(`\\[${escapeRegExp(key)}\\]`, 'g');
      result = result.replace(pattern, String(args[key]));
    });
    return result;
  }
}
